import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components/native";
import { Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { QuantitySelectorProps } from "./types";
import { showToast, ToastTypes } from "../VDBToast/util";
import { useTranslation } from "react-i18next";

const QuantitySelector = ({
  defaultValue = 1,
  min = 1,
  max = 99,
  onChange,
  label = "",
  fromOrderNow = false,
  disabled = false,
}: QuantitySelectorProps) => {
  if (max === 1) return null;

  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [quantity, setQuantity] = useState(defaultValue);
  const previousValue = useRef(defaultValue);

  useEffect(() => {
    setQuantity((current) =>
      current === defaultValue ? current : defaultValue
    );
  }, [defaultValue]);

  const parsedQty = Number(quantity) || min;

  const handleChange = (newValue: number) => {
    const value = Math.max(min, Math.min(max, newValue));
    onChange?.(value);
    previousValue.current = value;
    setQuantity(value);
  };

  const increment = () => {
    if (parsedQty < max) {
      handleChange(parsedQty + 1);
    }
  };

  const decrement = () => {
    if (parsedQty > min) {
      handleChange(parsedQty - 1);
    }
  };

  const handleInputChange = (text: string) => {
    if (/^\d*$/.test(text) && (text === "" || parseInt(text, 10) > 0)) {
      setQuantity(text);
    }
  };

  const handleEndEditing = () => {
    const value = Number(quantity);
    if (!value || value < min) {
      handleChange(min);
    } else if (value > max) {
      handleChange(max);
      showToast(
        undefined,
        t(
          "productDetailPage.quantitySelector.error.text",
          "Entered quantity is out of availability."
        ),
        ToastTypes.ERROR,
        true,
        true,
        0,
        () => null,
        true
      );
    } else {
      handleChange(value);
    }
  };

  return (
    <Container>
      {label && (
        <>
          <LabelContainer>
            <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
              {label}
            </VDBText>
          </LabelContainer>
          <Spacer size={SpaceType.Double} />
        </>
      )}
      <QuantityContainer>
        <CircleButton
          disabled={disabled || parsedQty === min}
          onPress={decrement}
        >
          <VDBIcon
            color={
              disabled || parsedQty === min
                ? theme["core-03"]
                : theme["core-05"]
            }
            size={24}
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.MINUS_CIRCLE}
          />
        </CircleButton>

        <Spacer size={fromOrderNow ? SpaceType.Tiny : SpaceType.Basic} />

        <QuantityInput
          value={String(quantity)}
          onChangeText={handleInputChange}
          onBlur={handleEndEditing}
          editable={!disabled}
          keyboardType="number-pad"
          returnKeyType="done"
          textAlign="center"
          maxLength={3}
          style={{
            ...typography["Body-M"],
          }}
        />

        <Spacer size={fromOrderNow ? SpaceType.Tiny : SpaceType.Basic} />

        <CircleButton
          disabled={disabled || parsedQty === max}
          onPress={increment}
        >
          <VDBIcon
            color={
              disabled || parsedQty === max
                ? theme["core-03"]
                : theme["core-05"]
            }
            size={24}
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.ADD_CIRCLE}
          />
        </CircleButton>
      </QuantityContainer>
    </Container>
  );
};

export default QuantitySelector;

const Container = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const LabelContainer = styled.View``;

const QuantityContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const CircleButton = styled.TouchableOpacity``;

const QuantityInput = styled.TextInput`
  width: 35px;
  height: 24px;
  color: ${({ theme }) => theme["core-05"]};
  text-align: center;
  padding: 0px;
`;
